import { Button, LinkButton } from '@/components/elements/forms/buttons';
import {
  getCookie,
  initAmplitudeAnalytics,
  isGoogleCrawler,
  isServer,
  sendPageView,
  setCookiePreferences,
} from '@/helpers';
import { _t } from '@/locale';
import { MouseEvent, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { initTracking } from '@/App.hooks';
import COOKIES from '@/constants/cookies';
import withAmplitudeExperiment from '@/hoc/withAmplitudeExperiment';
import { useAppDispatch } from '@/hooks';
import { AmplitudeExperimentContextType } from '@/hooks/useAmplitudeExperiment';
import { useConfidenceClient } from '@/hooks/useConfidenceExperiment';
import Modal from './redesign/Modal/Modal';

type AllowCookiesProps = { onAllowCallback?: () => void } & AmplitudeExperimentContextType;

const handleClickModal = (e: MouseEvent<HTMLDivElement>) => {
  e.stopPropagation();
  e.preventDefault();
};

const AllowCookies = ({ initialize: initializeAmplitudeExperiment, onAllowCallback }: AllowCookiesProps) => {
  const [show, setShow] = useState(true);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const confidenceClient = useConfidenceClient();
  const cookieAccepted = Boolean(getCookie(COOKIES.BOKADIREKT_COOKIE_PREFERENCES_MP));

  useEffect(() => {
    if (cookieAccepted) setShow(false);
  }, [cookieAccepted]);

  if (isServer) return null;
  if (window.location.pathname === '/articles/cookies') return null;
  if (isGoogleCrawler()) return null;

  const acceptCookie = () => {
    setCookiePreferences('Analytics', dispatch);
    setShow(false);
    initAmplitudeAnalytics(confidenceClient?.setDeviceId);
    initTracking();
    sendPageView();
    initializeAmplitudeExperiment();
    onAllowCallback?.();
  };

  return (
    <Modal isOpen={show} shouldCloseOnOverlayClick={false}>
      <Modal.Content floating>
        <Modal.Header title={_t('allowCookiesTitle')} />
        <div className="flex flex-col" onClick={handleClickModal}>
          <div className="pb-xxl text-md">
            <span className="text-black-600 whitespace-pre-wrap">{_t('allowCookiesContent')}&nbsp;</span>
            <Link
              to={{ pathname: '/articles/cookies', state: { fromAllowCookiesPopup: location.pathname } }}
              className="underline">
              {_t('readMore')}
              <span className="sr-only"> {_t('aboutOurCookiePolicy')}</span>
            </Link>
          </div>
          <div className="space-y-lg flex flex-col">
            <Button size="md" data-cy="allowCookiesButton" onClick={acceptCookie}>
              {_t('allowCookiesButton')}
            </Button>
            <LinkButton
              size="md"
              to={{ pathname: '/articles/cookies', state: { fromAllowCookiesPopup: location.pathname } }}
              style={{ textAlign: 'center' }}>
              {_t('customise')}
            </LinkButton>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default withAmplitudeExperiment(AllowCookies);
